// frontend/src/components/production-sequence/SequenceCardList.tsx
import { useTranslation } from 'react-i18next';
import { COMP_BADGE, STATUS_BADGE, STATUS_BADGE_BASE, fmtDate, fmtNum } from './sequenceFormat';
import { adherenceTextCls } from '@/utils/adherencia';
import { BarraMarcas, BotonMarca, useMarcadas, useMarcasFilas } from '@/components/common/MarcasFilas';
import { claveFila } from '@/utils/marcasFilas';
import type { ProductionOrder } from '@/types/productionSequence';

export function SequenceCardList({ orders, dark, avpOnly = false }: { orders: ProductionOrder[]; dark: boolean; avpOnly?: boolean }) {
  const { t } = useTranslation();
  const card = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  // Mismo ámbito que las tablas de escritorio: lo marcado en el móvil sigue
  // marcado al abrir la misma pantalla en una ventana ancha. Las tarjetas no son
  // `<tr>`, así que llevan el tinte y el botón a mano.
  const marcas = useMarcasFilas('secuencia');
  const marcadas = useMarcadas(marcas);
  const marcadoCls = dark ? 'fila-marcada-dark' : 'fila-marcada';

  if (avpOnly) {
    return (
      <div className="flex flex-col gap-2">
        <BarraMarcas dark={dark} marcas={marcas} className="rounded-lg border-b-0" />
        {orders.map(o => {
          const clave = claveFila(o.id);
          const marcada = marcadas.has(clave);
          return (
          <div key={o.id} data-seq-row={o.id} className={`rounded-xl border p-3 ${card} ${marcada ? marcadoCls : ''}`}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
              <span className="font-mono font-semibold text-[13px]">{o.id}</span>
            </div>
            <div className="text-[12px] font-semibold">{o.referencia}</div>
            <div className={`text-[12px] mb-2 ${muted}`}>{o.descripcion}</div>
            <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[11px]">
              <span className={muted}>{t('sequence.col.udOT')}: {fmtNum(o.udOT)}</span>
              <span className={muted}>{t('sequence.col.udProd')}: {fmtNum(o.udProducidas)}</span>
              <span className={muted}>{t('sequence.col.udPte')}: {fmtNum(o.udPendientes)}</span>
              <span className={`col-span-3 ${muted}`}>{t('sequence.col.fPlanFin')}: {fmtDate(o.fechaPlanificadaFin)} · {t('sequence.col.fRealFin')}: {fmtDate(o.fechaRealFin)}</span>
            </div>
          </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <BarraMarcas dark={dark} marcas={marcas} className="rounded-lg border-b-0" />
      {orders.map(o => {
        const clave = claveFila(o.id);
        const marcada = marcadas.has(clave);
        const adh = o.adherenciaPct;
        // Mismo criterio de tres bandas que la tabla (verde solo en el objetivo
        // 98–102 %). En la tarjeta se tiñe el texto en lugar de rellenar la píldora:
        // el blanco sobre ámbar claro no tiene contraste suficiente.
        const adhCls = adherenceTextCls(adh, dark);
        return (
          <div key={o.id} data-seq-row={o.id} className={`rounded-xl border p-3 ${card} ${marcada ? marcadoCls : ''}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5 font-mono font-semibold text-[13px]">
                <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
                {o.sequencePosition + 1}. {o.id}
              </span>
              <span className={`${STATUS_BADGE_BASE} ${STATUS_BADGE[o.estado]}`}>{t(`sequence.estado.${o.estado}`)}</span>
            </div>
            <div className="text-[12px] font-semibold mb-0.5">{o.referencia}</div>
            <div className={`text-[12px] mb-2 ${muted}`}>{o.descripcion}</div>
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${COMP_BADGE[o.componentesStatus]}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />{t(`sequence.componentStatus.${o.componentesStatus}`)}
              </span>
              <span className={muted}>{t('sequence.col.udPte')}: {o.udPendientes.toLocaleString('es-ES')}</span>
              {adh != null && (
                <span className={`font-semibold tabular-nums ${adhCls}`}>{adh.toFixed(0)}%</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
